import Vue from "vue";
import { ActionTree, MutationTree } from "vuex";
import { RootState } from "@/store/types";
import { ServerResult } from "@/core/http/server_result";
import moment from "moment";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import {
  isTodayOrFuture,
  outputToOpening,
  recalcDayOutputShift,
  rollForwardProductDays,
  shiftDate
} from "./recalcFutureDays";
import {
  applyStickerExpandedState,
  collectStickerExpandedMap,
  saveStickerExpandedMap
} from "./stickerExpandedState";

interface PendingOutput {
  stickerId: any;
  productContainerId: any;
  date: string;
  slot: any;
  quantity: number;
}

interface ProductionForecastDailyState {
  loaded: boolean;
  loading: boolean;
  fatalError: boolean;
  items: any[];
  days: any[];
  slots: any[];
  totals: any;
  meta: any;
  forecastGroup: string;
  lastQueryParams: any;
  pendingOutputs: Record<string, PendingOutput>;
  savingOutputs: boolean;
}

const DEFAULT_GROUP = "cheese";
const REPORT_DATA = "REPORT_DATA";

const state: ProductionForecastDailyState = {
  loaded: false,
  loading: false,
  fatalError: false,
  items: [],
  days: [],
  slots: [],
  totals: {},
  meta: {},
  forecastGroup: DEFAULT_GROUP,
  lastQueryParams: {},
  pendingOutputs: {},
  savingOutputs: false
};

function pendingOutputKey(date: string, productContainerId: any, slot: any) {
  return `${date}:${productContainerId}:${slot.key}`;
}

function formatNumber(value: any): number {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
}

function isNegative(value: any): boolean {
  return formatNumber(value) < 0;
}

function dayColumnCount(slots: any[]): number {
  return 6 + slots.length;
}

function buildFilter(params: any) {
  const anchor = params.date
    ? moment(params.date).format("YYYY-MM-DD")
    : moment().format("YYYY-MM-DD");

  return {
    date_from: anchor,
    date_to: anchor,
    forecast_group: params.forecastGroup || DEFAULT_GROUP
  };
}

function sumStickerDay(sticker: any, date: string, field: string): number {
  return (sticker.rows || []).reduce(
    (sum: number, row: any) => sum + formatNumber(row.days?.[date]?.[field]),
    0
  );
}

function recalcStickerDaysFromRows(rows: any[], days: any[], slots: any[]) {
  const stickerDays: any = {};

  days.forEach((day: any) => {
    const d = day.date;
    const slotValues: any = {};

    slots.forEach((slot: any) => {
      slotValues[slot.key] = {
        quantity: 0,
        volume_id: slot.volume_id,
        shift_code: slot.shift_code
      };
    });

    const dayEntry: any = {
      opening_balance: 0,
      shipment_deal: 0,
      shipment_movement: 0,
      close_plan: 0,
      close_with_output: 0,
      close_fact: 0,
      slot_values: slotValues
    };

    rows.forEach((row: any) => {
      const rowDay = row.days?.[d] || {};
      dayEntry.opening_balance += formatNumber(rowDay.opening_balance);
      dayEntry.shipment_deal += formatNumber(rowDay.shipment_deal);
      dayEntry.shipment_movement += formatNumber(rowDay.shipment_movement);
      dayEntry.close_plan += formatNumber(rowDay.close_plan);
      dayEntry.close_with_output += formatNumber(rowDay.close_with_output);
      dayEntry.close_fact += formatNumber(rowDay.close_fact);

      slots.forEach((slot: any) => {
        dayEntry.slot_values[slot.key].quantity += formatNumber(
          rowDay.slot_values?.[slot.key]?.quantity
        );
      });
    });

    stickerDays[d] = dayEntry;
  });

  return stickerDays;
}

function recalcAllStickerDays(items: any[], days: any[], slots: any[]) {
  return items.map((sticker: any) => ({
    ...sticker,
    days: recalcStickerDaysFromRows(sticker.rows || [], days, slots)
  }));
}

function xlsxNegStyle(cell: ExcelJS.Cell, value: any) {
  if (isNegative(value)) {
    cell.font = {
      ...(cell.font || {}),
      color: { argb: "FFFF3860" },
      bold: true
    };
  }
}

function addDayHeaders(
  worksheet: ExcelJS.Worksheet,
  days: any[],
  slots: any[]
) {
  worksheet.getCell(1, 1).value = "Продукт";
  worksheet.mergeCells(1, 1, 2, 1);

  let colIdx = 2;
  days.forEach((day: any) => {
    const span = dayColumnCount(slots);
    worksheet.mergeCells(1, colIdx, 1, colIdx + span - 1);
    worksheet.getCell(1, colIdx).value = moment(day.date).format("dd DD.MM");

    ["Ост. нач.", "З", "П", "План", "плюс выпуск", "Факт"].forEach(title => {
      worksheet.getCell(2, colIdx).value = title;
      colIdx += 1;
    });

    slots.forEach((slot: any) => {
      worksheet.getCell(2, colIdx).value = slot.label;
      colIdx += 1;
    });
  });
}

function writeDayValues(
  row: ExcelJS.Row,
  dayMap: any,
  days: any[],
  slots: any[]
) {
  let colIdx = 2;
  days.forEach((day: any) => {
    const dayData = dayMap?.[day.date] || {};
    [
      "opening_balance",
      "shipment_deal",
      "shipment_movement",
      "close_plan",
      "close_with_output",
      "close_fact"
    ].forEach(key => {
      const value = formatNumber(dayData[key]);
      const cell = row.getCell(colIdx);
      cell.value = value;
      xlsxNegStyle(cell, value);
      colIdx += 1;
    });

    slots.forEach((slot: any) => {
      const value = formatNumber(dayData.slot_values?.[slot.key]?.quantity);
      const cell = row.getCell(colIdx);
      cell.value = value;
      xlsxNegStyle(cell, value);
      colIdx += 1;
    });
  });
}

function fillWorksheet(
  worksheet: ExcelJS.Worksheet,
  items: any[],
  days: any[],
  slots: any[],
  totals: any
) {
  addDayHeaders(worksheet, days, slots);
  worksheet.views = [{ state: "frozen", xSplit: 1, ySplit: 2 }];
  worksheet.getColumn(1).width = 40;

  let rowIdx = 3;
  const totalRow = worksheet.getRow(rowIdx);
  totalRow.getCell(1).value = "Итого";
  totalRow.font = { bold: true };
  writeDayValues(totalRow, totals, days, slots);
  rowIdx += 1;

  items.forEach((sticker: any) => {
    const stickerRow = worksheet.getRow(rowIdx);
    stickerRow.getCell(1).value = sticker.sticker_name || "Без стикера";
    stickerRow.font = { bold: true };
    writeDayValues(stickerRow, sticker.days, days, slots);
    rowIdx += 1;

    if (!sticker.expanded) return;

    (sticker.rows || []).forEach((product: any) => {
      const productRow = worksheet.getRow(rowIdx);
      productRow.getCell(
        1
      ).value = `${product.product_name} ${product.product_container_name}`;
      writeDayValues(productRow, product.days, days, slots);
      rowIdx += 1;
    });
  });
}

export const actions: ActionTree<ProductionForecastDailyState, RootState> = {
  loadData({ commit }, params) {
    const vm = (this as any)._vm;
    const queryParams = buildFilter(params);

    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.log("[FIX] ProductionForecastDaily loadData", queryParams);
    }

    return new Promise((resolve, reject) => {
      commit("START_LOADING");
      commit("SET_LAST_QUERY_PARAMS", params);

      vm.$db([
        vm
          .$q(REPORT_DATA)
          .raw("production_forecast_daily")
          .action(queryParams)
      ])
        .then((result: ServerResult) => {
          const content = result.getOperation(REPORT_DATA).getContent();
          if (process.env.NODE_ENV !== "production") {
            const totalProductRows = (content?.items || []).reduce(
              (sum: number, sticker: any) => sum + (sticker.rows || []).length,
              0
            );
            // eslint-disable-next-line no-console
            console.debug("[ProductionForecastDaily] loadData", {
              items: (content?.items || []).length,
              totalProductRows,
              days: (content?.days || []).length,
              slots: (content?.slots || []).length
            });
          }
          commit("SET_REPORT_DATA", content);
          commit("LOADED_SUCCESS");
          resolve(content);
        })
        .catch((error: any) => {
          commit("LOADED_ERROR");
          reject(error);
        });
    });
  },

  toggleSticker({ commit }, stickerId: any) {
    commit("TOGGLE_STICKER", stickerId);
  },

  expandAllStickers({ commit }) {
    commit("SET_ALL_STICKERS_EXPANDED", true);
  },

  collapseAllStickers({ commit }) {
    commit("SET_ALL_STICKERS_EXPANDED", false);
  },

  stageOutputCell({ commit, state }, payload: any) {
    if (
      process.env.NODE_ENV !== "production" &&
      isTodayOrFuture(payload.date)
    ) {
      // eslint-disable-next-line no-console
      console.log("[FIX] ProductionForecastDaily stageOutputCell rollForward", {
        date: payload.date,
        productContainerId: payload.productContainerId,
        slot: payload.slot?.key,
        quantity: payload.quantity
      });
    }

    commit("PATCH_OUTPUT_CELL", payload);
    commit("SET_PENDING_OUTPUT", payload);
    commit("RECALC_TOTALS", state.slots);
  },

  savePendingOutputs({ commit, dispatch, state }) {
    const vm = (this as any)._vm;
    const pending = Object.values(state.pendingOutputs);

    if (!pending.length) {
      return Promise.resolve(null);
    }

    const body = {
      forecast_group: state.forecastGroup,
      entries: pending.map(entry => ({
        date: entry.date,
        product_container_id: entry.productContainerId,
        volume_id: entry.slot.volume_id,
        shift_code: entry.slot.shift_code,
        quantity: entry.quantity
      }))
    };

    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.log("[FIX] ProductionForecastDaily savePendingOutputs", {
        count: pending.length,
        entries: body.entries
      });
    }

    return new Promise((resolve, reject) => {
      commit("SET_SAVING_OUTPUTS", true);
      vm.$http
        .post("/api/v1/production_forecast_daily/upsert", body, {})
        .then(() => {
          commit("CLEAR_PENDING_OUTPUTS");
          return dispatch("loadData", state.lastQueryParams);
        })
        .then(() => {
          commit("SET_SAVING_OUTPUTS", false);
          resolve(null);
        })
        .catch((error: any) => {
          if (process.env.NODE_ENV !== "production") {
            // eslint-disable-next-line no-console
            console.error(
              "[FIX] ProductionForecastDaily savePendingOutputs failed",
              error
            );
          }
          commit("SET_SAVING_OUTPUTS", false);
          reject(error);
        });
    });
  },

  exportData({ state }) {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Планирование производства");
    fillWorksheet(
      worksheet,
      state.items,
      state.days,
      state.slots,
      state.totals
    );

    return workbook.xlsx.writeBuffer().then((buffer: ArrayBuffer) => {
      const label =
        state.meta?.date_from && state.meta?.date_to
          ? `${state.meta.date_from}_${state.meta.date_to}`
          : moment().format("YYYY-MM-DD");
      const filename = `production-forecast-daily-${state.forecastGroup}-${label}.xlsx`;
      saveAs(new Blob([buffer]), filename);
      return filename;
    });
  }
};

export const mutations: MutationTree<ProductionForecastDailyState> = {
  START_LOADING(state) {
    state.loaded = false;
    state.loading = true;
    state.fatalError = false;
  },
  LOADED_SUCCESS(state) {
    state.loaded = true;
    state.loading = false;
    state.fatalError = false;
  },
  LOADED_ERROR(state) {
    state.loaded = true;
    state.loading = false;
    state.fatalError = true;
  },
  SET_LAST_QUERY_PARAMS(state, params: any) {
    state.lastQueryParams = params;
  },
  SET_REPORT_DATA(state, data: any) {
    state.days = data.days || [];
    state.slots = data.slots || [];
    state.meta = data.meta || {};
    state.forecastGroup = data.meta?.forecast_group || DEFAULT_GROUP;
    state.pendingOutputs = {};

    const items = recalcAllStickerDays(
      data.items || [],
      state.days,
      state.slots
    );
    state.items = applyStickerExpandedState(items, state.forecastGroup);

    const totals: any = {};
    state.days.forEach((day: any) => {
      const d = day.date;
      totals[d] = {
        opening_balance: state.items.reduce(
          (s, st) => s + sumStickerDay(st, d, "opening_balance"),
          0
        ),
        shipment_deal: state.items.reduce(
          (s, st) => s + sumStickerDay(st, d, "shipment_deal"),
          0
        ),
        shipment_movement: state.items.reduce(
          (s, st) => s + sumStickerDay(st, d, "shipment_movement"),
          0
        ),
        close_plan: state.items.reduce(
          (s, st) => s + sumStickerDay(st, d, "close_plan"),
          0
        ),
        close_with_output: state.items.reduce(
          (s, st) => s + sumStickerDay(st, d, "close_with_output"),
          0
        ),
        close_fact: state.items.reduce(
          (s, st) => s + sumStickerDay(st, d, "close_fact"),
          0
        )
      };
    });
    state.totals = totals;
  },
  TOGGLE_STICKER(state, stickerId: any) {
    state.items = state.items.map((item: any) => {
      if (String(item.sticker_id) !== String(stickerId)) return item;
      return { ...item, expanded: !item.expanded };
    });
    saveStickerExpandedMap(
      state.forecastGroup,
      collectStickerExpandedMap(state.items)
    );
  },
  SET_ALL_STICKERS_EXPANDED(state, expanded: boolean) {
    state.items = state.items.map((item: any) => ({ ...item, expanded }));
    saveStickerExpandedMap(
      state.forecastGroup,
      collectStickerExpandedMap(state.items)
    );
  },
  PATCH_OUTPUT_CELL(state, payload: any) {
    const { stickerId, productContainerId, date, slot, quantity } = payload;

    state.items = state.items.map((sticker: any) => {
      if (String(sticker.sticker_id) !== String(stickerId)) return sticker;

      const rows = (sticker.rows || []).map((row: any) => {
        if (String(row.product_container_id) !== String(productContainerId))
          return row;
        const days = { ...row.days };
        const dayData = { ...(days[date] || {}) };
        const slotValues = { ...(dayData.slot_values || {}) };
        slotValues[slot.key] = {
          ...(slotValues[slot.key] || {}),
          quantity: formatNumber(quantity),
          volume_id: slot.volume_id,
          shift_code: slot.shift_code
        };
        dayData.slot_values = slotValues;
        days[date] = dayData;

        const forecastGroup = state.forecastGroup || DEFAULT_GROUP;
        let rolledDays = days;

        if (outputToOpening(forecastGroup)) {
          // Sauce: output(D) → opening(D+1) for future days.
          rolledDays = isTodayOrFuture(date)
            ? rollForwardProductDays(
                state.days,
                days,
                date,
                state.slots,
                forecastGroup
              )
            : days;
        } else {
          // Cheese/general: output(D) → close_with_output(D+1), then roll.
          const nextDate = shiftDate(date, 1);
          const dayDates = state.days.map((d: any) => d.date);
          const nextInView = dayDates.indexOf(nextDate) >= 0;

          if (nextInView) {
            recalcDayOutputShift(
              state.days,
              days,
              nextDate,
              state.slots,
              forecastGroup
            );
          }

          const rollFrom = nextInView ? nextDate : date;
          rolledDays = isTodayOrFuture(rollFrom)
            ? rollForwardProductDays(
                state.days,
                days,
                rollFrom,
                state.slots,
                forecastGroup
              )
            : days;
        }

        return { ...row, days: rolledDays };
      });

      return {
        ...sticker,
        rows,
        days: recalcStickerDaysFromRows(rows, state.days, state.slots)
      };
    });
  },
  RECALC_TOTALS(state, slots: any[]) {
    const totals: any = {};
    state.days.forEach((day: any) => {
      const d = day.date;
      totals[d] = {
        opening_balance: state.items.reduce(
          (s, st) => s + sumStickerDay(st, d, "opening_balance"),
          0
        ),
        shipment_deal: state.items.reduce(
          (s, st) => s + sumStickerDay(st, d, "shipment_deal"),
          0
        ),
        shipment_movement: state.items.reduce(
          (s, st) => s + sumStickerDay(st, d, "shipment_movement"),
          0
        ),
        close_plan: state.items.reduce(
          (s, st) => s + sumStickerDay(st, d, "close_plan"),
          0
        ),
        close_with_output: state.items.reduce(
          (s, st) => s + sumStickerDay(st, d, "close_with_output"),
          0
        ),
        close_fact: state.items.reduce(
          (s, st) => s + sumStickerDay(st, d, "close_fact"),
          0
        )
      };
    });
    state.totals = totals;
    void slots;
  },
  SET_PENDING_OUTPUT(state, payload: any) {
    const key = pendingOutputKey(
      payload.date,
      payload.productContainerId,
      payload.slot
    );
    Vue.set(state.pendingOutputs, key, {
      stickerId: payload.stickerId,
      productContainerId: payload.productContainerId,
      date: payload.date,
      slot: payload.slot,
      quantity: formatNumber(payload.quantity)
    });
  },
  CLEAR_PENDING_OUTPUTS(state) {
    state.pendingOutputs = {};
  },
  SET_SAVING_OUTPUTS(state, value: boolean) {
    state.savingOutputs = value;
  }
};

export const STORE_KEY = "$_production_forecast_daily";

export const store = {
  namespaced: true,
  state,
  actions,
  mutations
};
